/**
 * @author       luis.orihuela@agileengine.com (Luis Orihuela)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  format params for rec engine query
 */
import { MNeighborhood } from '@zcode/nr/es/shared/bizos/neighborhood'
import type { MNeighborhood as MNeighborhoodType } from '@zcode/state-type'

import {
  AMENITIES_LAUNDRY_KEYS,
  AMENITIES_PARKING_KEYS,
} from '../../configs/amenitiesConfig/constants'
import type { ParamsType } from '../../types'

import { formatLeaseLength } from './formatLeaseLength'
import { formatBedroomsList } from './formatParams'

export const PROPERTY_TYPES = {
  unknown: 0,
  condo: 1,
  townhouse: 2,
  apartment: 4,
  house: 13,
  room: 16,
}

export const LEASE_LENGTH_VALUES = {
  'A year or more': 'long_term',
  '6-11 months': 'long_term',
  '1-6 months': 'monthly',
  'Weekly or nightly': 'vacation',
}

export const formatAmenities = (amenities: readonly string[] = []) => {
  const buildingAmenities: number[] = []
  const listingAmenities: number[] = []
  for (const amenity of amenities) {
    if (amenity.includes('b')) {
      buildingAmenities.push(Number.parseInt(amenity.substring(2)))
    }
    if (amenity.includes('l')) {
      listingAmenities.push(Number.parseInt(amenity.substring(2)))
    }
  }
  return {
    buildingAmenities,
    listingAmenities,
    allAmenities: buildingAmenities.concat(listingAmenities),
  }
}

export const formatRequiredAmenities = (
  amenities: readonly string[],
  parking?: readonly string[],
  laundry?: readonly string[]
) => {
  const format = (item: string) => item.split('-').join('')

  return amenities.reduce((accumulator: string[], amenity: string) => {
    if (amenity === 'laundry' && laundry?.length) {
      for (const laundryType of laundry) {
        accumulator.push(format(laundryType))
      }
    } else if (amenity === 'parking' && parking?.length) {
      for (const parkingType of parking) {
        accumulator.push(format(parkingType))
      }
    } else {
      accumulator.push(format(amenity))
    }
    return accumulator
  }, [])
}

const formatPetsParams = (pets: readonly string[]) => {
  if (pets[0] === 'Yes') {
    return [1, 2, 3]
  }
  return []
}

const formatPropertyTypes = (propertyCategories: [] = []) => {
  return propertyCategories.reduce(
    (accumulator: number[], currentValue: keyof typeof PROPERTY_TYPES) => {
      accumulator.push(PROPERTY_TYPES[currentValue])
      return accumulator
    },
    []
  )
}

export const formatParamsForRecEngine = (params: ParamsType) => {
  const {
    bedrooms,
    finalAmenities,
    finalPrice,
    leaseLength,
    pets,
    propertyCategories,
    requiredAmenities,
    nearbyHoods,
  } = params
  const formattedParams: { [key: string]: any } = {
    limit: 10,
    max_price: finalPrice,
    ...formatLeaseLength(leaseLength, LEASE_LENGTH_VALUES),
  }
  const { buildingAmenities, listingAmenities } = formatAmenities(finalAmenities)
  if (listingAmenities.length) {
    formattedParams.amenities = listingAmenities
  }
  if (buildingAmenities.length) {
    formattedParams.building_amenities = buildingAmenities
  }
  formattedParams.bedrooms = formatBedroomsList(bedrooms)
  formattedParams.property_types = formatPropertyTypes(propertyCategories)
  if (pets?.length) {
    formattedParams.pets = formatPetsParams(params?.pets)
  }

  const findMapAmenities = (keys: string[]) =>
    (finalAmenities ?? []).filter((amenity: string) => keys.includes(amenity))

  if (requiredAmenities?.length) {
    formattedParams.required_amenities = formatRequiredAmenities(
      requiredAmenities,
      findMapAmenities(AMENITIES_PARKING_KEYS),
      findMapAmenities(AMENITIES_LAUNDRY_KEYS)
    )
  }

  formattedParams.neighborhoods = nearbyHoods?.map(({ path }: MNeighborhoodType) => path)

  formattedParams.neighborhood_ids = nearbyHoods?.map((hood: any) => {
    const { neighborhoodId } = (
      hood instanceof MNeighborhood ? hood : MNeighborhood.fromJS(hood)
    ) as MNeighborhoodType

    return neighborhoodId
  })

  return { ...formattedParams }
}
